import { useToast } from "@lib/hooks/useToast";
import Papa from "papaparse";
import { useCallback, useState } from "react";

function getNestedValue(obj: Record<string, any>, path: string): unknown {
  if (!path || !obj) return "";
  const keys = path.split(".");
  let value: any = obj;
  for (const key of keys) {
    if (value === null || value === undefined) return "";
    value = value[key];
  }
  return value !== null && value !== undefined ? value : "";
}

function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "Oui" : "Non";
  if (value instanceof Date) return value.toLocaleDateString("fr-FR");
  return String(value);
}

interface CsvColumn {
  accessorKey?: string;
  id?: string;
  header?: string | { title: string };
  cell?: (info: any) => React.ReactNode;
}

interface UseCsvExportOptions {
  data: Record<string, any>[];
  columns: CsvColumn[];
  filename?: string;
}

interface ExportOptions {
  selectedColumns?: string[];
  customFilename?: string;
  selectedData?: Record<string, any>[];
}

export function useCsvExport({ data, columns, filename = "export.csv" }: UseCsvExportOptions) {
  const [isExporting, setIsExporting] = useState(false);
  const toast = useToast();

  const exportCsv = useCallback(
    (options: ExportOptions = {}) => {
      setIsExporting(true);

      try {
        const exportData = options.selectedData || data;
        const exportColumns = options.selectedColumns
          ? columns.filter((col) =>
              options.selectedColumns!.includes(col.accessorKey || col.id || ""),
            )
          : columns;

        if (!exportData || exportData.length === 0) {
          toast.warning("Aucune donnée à exporter");
          setIsExporting(false);
          return;
        }

        const csvData = exportData.map((row) => {
          const csvRow: Record<string, string> = {};

          exportColumns.forEach((col) => {
            const key = col.accessorKey || col.id || "";
            const header = typeof col.header === "string" ? col.header : col.header?.title || key;
            const value = col.accessorKey ? getNestedValue(row, col.accessorKey) : "";
            csvRow[header] = formatCellValue(value);
          });

          return csvRow;
        });

        const csv = Papa.unparse(csvData, {
          delimiter: ";",
          header: true,
          newline: "\r\n",
          encoding: "utf-8",
        });

        const bom = "\uFEFF";
        const csvWithBom = bom + csv;

        const blob = new Blob([csvWithBom], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = options.customFilename || filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(url);

        toast.success(`${exportData.length} ligne(s) exportée(s)`, { dedupeKey: "csv-export" });
      } catch (error) {
        console.error("CSV export error:", error);
        toast.error("Erreur lors de l'export CSV");
      } finally {
        setIsExporting(false);
      }
    },
    [data, columns, filename, toast],
  );

  const availableColumns = columns
    .filter((col) => col.accessorKey || col.id)
    .map((col) => ({
      id: col.accessorKey || col.id || "",
      label:
        typeof col.header === "string"
          ? col.header
          : col.header?.title || col.accessorKey || col.id || "",
    }));

  return {
    exportCsv,
    isExporting,
    availableColumns,
  };
}

export default useCsvExport;
